"use client";

import { Observable } from "@legendapp/state";
import { use$ } from "@legendapp/state/react";
import { useObservable } from "@legendapp/state/react";
import { filter } from "lodash";
import { PropsWithChildren, useEffect } from "react";

import { createCustomContext } from "./context";
import { useUndressContext } from "./UndressProvider";

export enum ETool {
  RUBBER = "rubber",
  BRUSH = "brush",
  NONE = "none",
}

interface Props {
  isShowDraw: boolean;
  onToggleShowDraw: () => void;
  brushSize: number;
  setBrushSize: (size: number) => void;
  currentTool: ETool;
  setTool: (tool: ETool) => void;
  isUndoDraw: boolean;
  setUndoDraw: (isUndo: boolean) => void;
  isCleanDraw: boolean;
  setCleanDraw: (isClean: boolean) => void;
  loadingIds: string[];
  isMaskLoading: boolean;
  startLoading: (id: string) => void;
  stopLoading: (id: string) => void;
}

const [Context, useAutoMaskContext] = createCustomContext<Observable<Props>>({
  name: "AutoMaskContext",
});
export { useAutoMaskContext };

export const AutoMaskProvider = ({ children }: PropsWithChildren) => {
  const { setLoading } = useUndressContext();

  const state$ = useObservable<Props>({
    isShowDraw: false,
    onToggleShowDraw: (): void => state$.isShowDraw.toggle(),
    brushSize: 10,
    setBrushSize: (size: number) => {
      state$.brushSize.set(size);
    },
    currentTool: ETool.BRUSH,
    setTool: (tool: ETool) => {
      state$.currentTool.set(tool);
    },
    isUndoDraw: false,
    setUndoDraw: (isUndo: boolean) => {
      state$.isUndoDraw.set(isUndo);
    },
    isCleanDraw: false,
    setCleanDraw: (isClean: boolean) => {
      state$.isCleanDraw.set(isClean);
    },
    loadingIds: [] as string[],
    isMaskLoading: false,
    startLoading: (id: string) => {
      state$.loadingIds.set((prev: string[]) => [...prev, id]);
      state$.isMaskLoading.set(true);
    },
    stopLoading: (id: string) => {
      state$.loadingIds.set((prev: string[]) =>
        filter(prev, (loadingId) => loadingId !== id),
      );
      state$.isMaskLoading.set(state$.loadingIds.get().length > 0);
    },
  });

  const isMaskLoading = use$(state$.isMaskLoading);

  useEffect(() => {
    setLoading(isMaskLoading);
  }, [setLoading, isMaskLoading]);

  return <Context value={state$}>{children}</Context>;
};
